import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IntervalType, SlotListArray, TimeSlot, ValidationErrorKeys, WrongCityWarning } from '@checkout/const';
import { CheckoutValidationService, DeliveryAddressService, ReceiptService, TimeSlotsService } from '@checkout/feature';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DeviceService } from '@shared/services';
import { ScrollToService } from '@shared/v2';
import { ModalService } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, take, withLatestFrom } from 'rxjs/operators';
import { SdvorTimeSlotDatepickerModalComponent } from './time-slot-datepicker-modal/time-slot-datepicker-modal.component';

@Component({
  selector: 'sd-timeslot-list',
  templateUrl: './time-slot-list.component.html',
  styleUrls: ['./time-slot-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorTimeSlotListComponent implements OnInit {
  isMobile$: Observable<boolean>;
  deliveryType$: Observable<string>;

  isManagerWillClarify$: Observable<boolean>;
  isLoading$: Observable<boolean>;
  currentTimeSlot$: Observable<TimeSlot>;

  dateRange$: Observable<{ min: Date; max: Date }>;
  ngbDateRangeMin$: Observable<NgbDateStruct>;
  ngbDateRangeMax$: Observable<NgbDateStruct>;

  validationErrors$: Observable<string[] | undefined>;

  timeSlots$ = new BehaviorSubject<SlotListArray[]>([]);
  customDate$ = new BehaviorSubject<string | null>(null);
  tabIndex$ = new BehaviorSubject<number>(0);
  addressCityMatches$: Observable<WrongCityWarning[]>;

  constructor(
    private deviceService: DeviceService,
    private receiptService: ReceiptService,
    private timeSlotsService: TimeSlotsService,
    private checkoutValidationService: CheckoutValidationService,
    private viewRef: ViewContainerRef,
    private scrollToService: ScrollToService,
    private modalService: ModalService,
    private deliveryAddressService: DeliveryAddressService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.deviceService.isMobileSmDown$;
    this.deliveryType$ = this.receiptService.activeReceiptTypeId$;

    this.isManagerWillClarify$ = this.timeSlotsService.isEmpty();
    this.isLoading$ = this.timeSlotsService.getLoading();
    this.currentTimeSlot$ = this.timeSlotsService.getCurrentTimeSlot();

    this.dateRange$ = this.timeSlotsService.getTimeSlots().pipe(
      filter((slots: SlotListArray[]) => slots.length > 0),
      map((slots: SlotListArray[]) => {
        const minDate = new Date(
          Math.min.apply(
            null,
            slots.map((slot) => new Date(slot.date)),
          ),
        );
        const min = new Date(minDate);
        const max = new Date(minDate);

        // Максимальная дата в календаре
        max.setDate(minDate.getDate() + 8);

        return { min, max };
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.ngbDateRangeMin$ = this.dateRange$.pipe(map(({ min }) => this.dateToNgbDateStruct(min)));
    this.ngbDateRangeMax$ = this.dateRange$.pipe(map(({ max }) => this.dateToNgbDateStruct(max)));

    this.validationErrors$ = this.checkoutValidationService
      .getErrors(ValidationErrorKeys.TIME_SLOT_REQUIRED)
      .pipe(map((errors) => errors && Object.values(errors)));

    this.timeSlotsService
      .getTimeSlots()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((slotListArray: SlotListArray[]): void => {
        this.timeSlots$.next(slotListArray);
      });

    this.addressCityMatches$ = this.deliveryAddressService.addressCityMatches$;

    combineLatest({
      tabIndex: this.tabIndex$,
      slots: this.timeSlots$,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ slots, tabIndex }) => {
        if (tabIndex >= 0 && slots.length) {
          const groups = Object.values(slots[tabIndex].groups);
          const filteredGroups = groups.filter((group) => group.length > 0);

          return filteredGroups.length && filteredGroups.length > 1
            ? this.timeSlotsService.setTimeSlot(undefined)
            : null;
        }
      });

    this.timeSlots$
      .pipe(withLatestFrom(this.tabIndex$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([slots, tabIndex]: [SlotListArray[], number]) => {
        if (slots.length && slots.length <= tabIndex) {
          this.tabIndex$.next(0);
        }
      });

    this.checkoutValidationService
      .getScrollToEvent(ValidationErrorKeys.TIME_SLOT_REQUIRED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scrollToService.scrollToElement(this.viewRef.element.nativeElement, {
          offset: -100,
        });
      });
  }

  openModal(): void {
    const modal = this.modalService.open(SdvorTimeSlotDatepickerModalComponent, { size: 'xl' });
    const componentInstance: SdvorTimeSlotDatepickerModalComponent = modal.componentInstance;
    componentInstance.minDate$ = this.ngbDateRangeMin$;
    componentInstance.maxDate$ = this.ngbDateRangeMax$;
    componentInstance.markDisabled = this.ngbDateRangeMarkDisabled;
    modal.result.then((result: NgbDateStruct | string) => {
      if (typeof result !== 'string') this.onSelectAnotherDateNgb(result);
    });
  }

  setTimeSlot(timeSlot: TimeSlot): void {
    this.currentTimeSlot$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((currentTimeSlot: TimeSlot) => {
      if (currentTimeSlot || timeSlot) {
        this.timeSlotsService.setTimeSlot(timeSlot);
      }
    });
  }

  setCurrentTab(index: number): void {
    if (index !== this.tabIndex$.value) {
      this.tabIndex$.next(index);
    }
  }

  onSelectAnotherDateNgb(ngbDate: NgbDateStruct): void {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    this.setAnotherDate(`${year}-${month}-${day}`);
  }

  ngbDateRangeMarkDisabled(date: NgbDateStruct): boolean {
    // Запрет выбора доставки 1 Января в календаре
    return date.month === 1 && date.day === 1;
  }

  isTabDisabled(rawDate: string): boolean {
    // Запрет выбора доставки 1 Января в табах
    const date = new Date(rawDate);

    return date.getDate() === 1 && date.getMonth() === 0;
  }

  private setAnotherDate(date: string): void {
    this.timeSlots$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((timeSlotsArray) => {
      const foundIndex = timeSlotsArray.findIndex((i) => i.date === date);

      // если выбрана дата, которая уже есть в массиве, то выделить ее
      if (foundIndex >= 0) {
        this.setCurrentTab(foundIndex);

        return;
      }

      if (this.customDate$.value) {
        timeSlotsArray.pop();
      }

      this.customDate$.next(date);

      if (date) {
        const previous: SlotListArray = timeSlotsArray[timeSlotsArray.length - 1];
        const next: SlotListArray = {
          date,
          groups: Object.entries(previous.groups).reduce(
            (groups, [key, timeSlots]) => {
              groups[key as IntervalType] = timeSlots.map((timeSlot) => ({ ...timeSlot, date: date }));

              return groups;
            },
            {} as SlotListArray['groups'],
          ),
        };

        this.timeSlots$.next([...timeSlotsArray, next]);

        this.setCurrentTab(this.timeSlots$.getValue().length - 1);
      } else {
        this.timeSlots$.next(timeSlotsArray);
      }
    });
  }

  private dateToNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }
}
